import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { Customer } from '@/types/customer';
import { customerService } from '@/services/customerService';
import { CustomerAccount } from '@/services/socialMediaService';
import { useAuthStore } from './authStore';
import { canAccessSupportPage } from '../utils/permissions';

export interface AccountSelection {
  type: 'all' | 'individual';
  customerId: string;
  accountId?: string; // Only present when type is 'individual'
  customer: Customer;
  account?: CustomerAccount; // Only present when type is 'individual'
}

interface GlobalCustomerState {
  // State
  selectedCustomer: Customer | null;
  selectedAccount: AccountSelection | null;
  customers: Customer[];
  customerAccounts: { [customerId: string]: CustomerAccount[] };
  loadingCustomers: boolean;
  loadingAccounts: { [customerId: string]: boolean };
  
  // Actions
  setSelectedCustomer: (customer: Customer | null) => void;
  setSelectedAccount: (selection: AccountSelection | null) => void;
  clearSelection: () => void;
  loadCustomers: () => Promise<void>;
  loadCustomerAccounts: (customerId: string) => Promise<void>;
  getAPIFilterParams: () => { customerId?: string; accountIds?: string[] };
}

const useGlobalCustomerStore = create<GlobalCustomerState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        selectedCustomer: null,
        selectedAccount: null,
        customers: [],
        customerAccounts: {},
        loadingCustomers: false,
        loadingAccounts: {},

        // Actions
        setSelectedCustomer: (customer) => {
          set({ selectedCustomer: customer, selectedAccount: null });
        },

        setSelectedAccount: (selection) => {
          set({ 
            selectedAccount: selection,
            selectedCustomer: selection?.customer || null
          });
        },

        clearSelection: () => {
          set({ 
            selectedCustomer: null,
            selectedAccount: null
          });
        },

        loadCustomers: async () => {
          set({ loadingCustomers: true });
          try {
            // Call customers API with filters: only active customers with categoryIds 1,2
            const response = await customerService.getCustomers({
              isActive: true,
              categoryIds: '1,2'
            });
            const customers = response.data.customers;

            // Filter customers based on user permissions
            const user = useAuthStore.getState().user;
            let filteredCustomers = customers;

            // Check if user has access:support permission
            const hasAccessSupport = canAccessSupportPage(user);

            if (!hasAccessSupport) {
              if (user?.customerId) {
                // Users with customerId only see their associated customer
                filteredCustomers = customers.filter(c => c.id === user.customerId);
              } else {
                // Users without customerId and without support access should not see any customers
                filteredCustomers = [];
              }
            }
            // Users with access:support permission see all customers
            
            // Extract platform accounts from customers and store them
            const customerAccounts: { [customerId: string]: CustomerAccount[] } = {};
            filteredCustomers.forEach(customer => {
              if (customer.platformAccounts) {
                customerAccounts[customer.id] = customer.platformAccounts.map(account => ({
                  id: account.id,
                  username: account.username,
                  displayName: account.displayName || '',
                  isActive: account.isActive,
                  platformId: account.platform.id,
                  platformName: account.platform.platformName,
                  platformDisplayName: account.platform.platformDisplayName,
                  categoryId: account.categoryId,
                  categoryName: account.categoryName,
                  followerCount: account.followerCount,
                  verified: account.verified,
                }));
              }
            });
            
            set({ 
              customers: filteredCustomers,
              customerAccounts,
              loadingCustomers: false 
            });
          } catch (error) {
            set({ loadingCustomers: false });
          }
        },

        loadCustomerAccounts: async () => {
          // Customer accounts are now loaded with the main customers call
          // This method is kept for compatibility but does nothing
        },

        getAPIFilterParams: () => {
          const { selectedAccount } = get();
          
          if (!selectedAccount) {
            return {};
          }

          if (selectedAccount.type === 'all') {
            return { customerId: selectedAccount.customerId };
          } else {
            return { 
              customerId: selectedAccount.customerId,
              accountIds: [selectedAccount.accountId!] 
            };
          }
        },
      }),
      {
        name: 'global-customer-store',
        partialize: () => ({}), // Don't persist customer selection
      }
    )
  )
);

export default useGlobalCustomerStore;